<script setup lang="ts">
import {
  closeModal,
  iBadge,
  iBadgeColours,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconSize,
  iKeyIconStyle,
  iKeyIconType,
  iModal,
  iSearchInput,
  openModal,
  showToast,
} from '@inscribe/signal';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import WorkspaceCreationModal from '@/V2/settings/WorkspaceCreationModal';

interface Workspace {
  id: string;
  name: string;
  is_geoblocked: boolean;
}

interface WorkspaceItem {
  id: string;
  name: string;
  isCurrentWorkspace: boolean;
  is_geoblocked: boolean;
}

/** Store */
const store = useStore();

/** Refs */
const searchQuery = ref<string>('');
const showCreationModal = ref<boolean>(false);

/** Computed */
const currentUser = computed(() => store.state.currentUser);
const workspaces = computed<Workspace[]>(() => store.state.workspaces);
const orgAdminAndCreationEnabled = computed<boolean>(
  () => store.getters.isOrgAdminAndWorkspacesCreationEnabled,
);

const workspaceItems = computed<WorkspaceItem[]>(() => {
  return workspaces.value
    .map((org) => ({
      name: org.name,
      id: org.id,
      isCurrentWorkspace: org.name === currentUser.value?.organisation?.name,
      is_geoblocked: org.is_geoblocked,
    }))
    .sort(
      (a, b) => Number(b.isCurrentWorkspace) - Number(a.isCurrentWorkspace),
    );
});

const filteredWorkspaces = computed<WorkspaceItem[]>(() => {
  if (!searchQuery.value.trim()) {
    return workspaceItems.value;
  }
  const query = searchQuery.value.toLowerCase().trim();
  return workspaceItems.value.filter((workspace) =>
    workspace.name.toLowerCase().includes(query),
  );
});

const hasNoResults = computed<boolean>(() => {
  return (
    searchQuery.value.trim() !== '' && filteredWorkspaces.value.length === 0
  );
});

/** Handlers */
function onWorkspaceSearchQueryChanged(value: string) {
  searchQuery.value = value;
}

function onModalOpen() {
  searchQuery.value = '';
}

function switchAction(orgId: string) {
  const workspace = workspaceItems.value.find(
    (workspace) => workspace.id === orgId,
  );
  if (!workspace) {
    return;
  } else if (workspace.isCurrentWorkspace) {
    closeSwitcherModal();
    return;
  }
  store
    .dispatch('switchWorkspace', orgId)
    .then((response: { data: { redirect_url?: string } }) => {
      const { redirect_url } = response.data;
      window.location.href = redirect_url || '/';
      if (!redirect_url || !redirect_url.includes('?sso=1')) {
        window.location.reload();
      }
    })
    .catch(() => {
      showToast({
        text: 'Error switching workspace.',
        isError: true,
      });
    });
}

function openCreateModal() {
  showCreationModal.value = true;
}

function createNewWorkspace() {
  closeSwitcherModal();
  openCreateModal();
}
function onCloseCreateModal() {
  showCreationModal.value = false;
}

function openSwitcherModal() {
  openModal('workspace-switcher-modal');
}

function closeSwitcherModal() {
  closeModal('workspace-switcher-modal');
}

defineExpose({
  openSwitcherModal,
  closeSwitcherModal,
  openCreateModal,
});
</script>

<template>
  <div class="workspace-switcher">
    <i-modal
      modal-id="workspace-switcher-modal"
      title="Switch Workspace"
      :show-footer="orgAdminAndCreationEnabled"
      :close-on-click-outside="!currentUser?.is_geoblocked"
      :has-padding="false"
      :with-body="false"
      class="workspace-switcher-modal"
      @on-open="onModalOpen"
    >
      <template #header>
        <div class="header d-flex flex-column">
          <i-search-input
            class="i-p-10"
            size="small"
            input-placeholder="Search workspaces"
            :model-value="searchQuery"
            :keyboard-shortcut="false"
            data-testid="workspace-search-input"
            data-dd-action-name="workspace-search-input"
            @update:model-value="onWorkspaceSearchQueryChanged"
          />
        </div>
      </template>

      <template #body-content>
        <div class="title i-text-body-2 i-px-10 i-pt-8 i-mb-4">Workspaces</div>
        <div class="workspaces-list i-px-8 i-pb-8">
          <div
            v-if="hasNoResults"
            class="no-results i-text-neutral-100 i-py-8 text-center"
          >
            No workspaces found
          </div>

          <button
            v-for="workspace in filteredWorkspaces"
            :key="workspace.id"
            class="workspace-button d-flex align-items-center i-gap-6 w-100 i-p-4 i-radius-2xl justify-content-between"
            :class="{
              current: workspace.isCurrentWorkspace,
            }"
            :disabled="workspace.is_geoblocked"
            data-testid="workspace-item"
            @click="switchAction(workspace.id)"
          >
            <i-key-icon
              icon="medium_dashboard_4"
              :shape="iKeyIconShape.SQUARE"
              :type="iKeyIconType.NEUTRAL"
              :size="iKeyIconSize.SM"
              :icon-style="iKeyIconStyle.OUTLINE"
            />
            <span
              class="workspace-name text-truncate i-text-subheading-2 flex-grow-1 d-flex align-items-start"
              >{{ workspace.name }}</span
            >
            <i-badge
              v-if="workspace.isCurrentWorkspace"
              :colour="iBadgeColours.NEUTRAL"
              label="Current"
              size="small"
            ></i-badge>
            <i-badge
              v-if="workspace.is_geoblocked"
              :colour="iBadgeColours.NEUTRAL"
              :outline="true"
              label="Geo-Restricted"
              :has-leading-icon="true"
              icon="small-lock"
              size="small"
            ></i-badge>
          </button>
        </div>
      </template>
      <template #footer>
        <div class="footer i-p-4">
          <i-button
            label="New Workspace"
            :type="iButtonTypes.NEUTRAL"
            :button-style="iButtonStyles.GHOST"
            :size="iButtonSizes.LARGE"
            class="create-new-workspace-button"
            left-icon="medium-add-3"
            @button-click="createNewWorkspace"
          />
        </div>
      </template>
    </i-modal>

    <workspace-creation-modal
      v-if="showCreationModal"
      data-testid="create-workspace-modal"
      @close-create-workspace-modal="onCloseCreateModal"
    />
  </div>
</template>

<style lang="scss">
.workspace-switcher-modal .modal-content {
  width: 670px !important;
  gap: 0;

  .title {
    color: var(--text-neutral);
  }

  .workspaces-list {
    height: 400px;
    overflow-y: auto;
  }

  .header {
    height: 72px;
    border-bottom: 1px solid var(--border-neutral-subtle);

    .i-search-input {
      border-radius: 20px;
      height: 100%;
      border: 0;
      font-size: 1.25rem;
    }
  }

  .workspace-button {
    border: 0;
    height: 52px;
    background-color: transparent;

    &:hover {
      background-color: var(--surface-neutral-subtle);
    }
  }

  .footer {
    border-top: 1px solid var(--border-neutral-subtle);

    .create-new-workspace-button {
      width: 100%;

      .label {
        width: 115px;
      }
    }
  }

  .no-results {
    color: var(--text-neutral-subtle);
  }
}
</style>
